import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export type VoiceState = 'idle' | 'playing' | 'paused';

@Injectable({
  providedIn: 'root',
})
export class SpeechSynthesisService {
  private synthesis: SpeechSynthesis;
  private utterance: SpeechSynthesisUtterance | null = null;
  private currentVoiceState = new BehaviorSubject<VoiceState>('idle');
  private currentPlaybackPosition = 0;
  private currentText = '';

  voiceState$ = this.currentVoiceState.asObservable();

  constructor() {
    this.synthesis = window.speechSynthesis;
    this.setupEventListeners();
  }

  private setupEventListeners() {
    // Reset state when speech is done
    document.addEventListener('visibilitychange', () => {
      if (document.hidden && this.isPlaying()) {
        this.pause();
      }
    });
  }

  isPlaying(): boolean {
    return this.currentVoiceState.value === 'playing';
  }

  isPaused(): boolean {
    return this.currentVoiceState.value === 'paused';
  }

  /**
   * Preprocess text to make it sound more natural when spoken
   */
  private preprocessText(text: string): string {
    return (
      text
        // Add pauses after sentences for better pacing
        .replace(/\. /g, '. ')
        .replace(/! /g, '! ')
        .replace(/\? /g, '? ')
        // Handle abbreviations more naturally
        .replace(/\bU\.S\.A?\b/g, 'United States')
        .replace(/\bU\.K\b/g, 'United Kingdom')
        .replace(/\bDr\./g, 'Doctor')
        .replace(/\bMr\./g, 'Mister')
        .replace(/\bMrs\./g, 'Missus')
        .replace(/\bMs\./g, 'Miss')
        .replace(/\betc\./g, 'etcetera')
        .replace(/\be\.g\./g, 'for example')
        .replace(/\bi\.e\./g, 'that is')
        // Handle numbers and dates more naturally
        .replace(/\b(\d{4})\b/g, (match, year) => {
          const num = parseInt(year);
          if (num >= 1000 && num <= 2100) {
            return year; // Keep years as is for natural pronunciation
          }
          return match;
        })
        // Add slight pauses for better readability
        .replace(/,\s/g, ', ')
        .replace(/:\s/g, ': ')
        .replace(/;\s/g, '; ')
        // Handle parenthetical content
        .replace(/\(/g, ' (')
        .replace(/\)/g, ') ')
        // Clean up multiple spaces
        .replace(/\s+/g, ' ')
        .trim()
    );
  }

  /**
   * Find the best available voice for natural speech
   */
  private selectBestVoice(): SpeechSynthesisVoice | null {
    const voices = this.synthesis.getVoices();

    // Priority order for more natural-sounding voices
    const voicePreferences = [
      // macOS/iOS high-quality voices
      (v: SpeechSynthesisVoice) => v.name.includes('Samantha') && v.lang.startsWith('en'),
      (v: SpeechSynthesisVoice) => v.name.includes('Alex') && v.lang.startsWith('en'),
      (v: SpeechSynthesisVoice) => v.name.includes('Victoria') && v.lang.startsWith('en'),
      (v: SpeechSynthesisVoice) => v.name.includes('Karen') && v.lang.startsWith('en'),

      // Windows high-quality voices
      (v: SpeechSynthesisVoice) => v.name.includes('Zira') && v.lang.startsWith('en'),
      (v: SpeechSynthesisVoice) => v.name.includes('David') && v.lang.startsWith('en'),
      (v: SpeechSynthesisVoice) => v.name.includes('Mark') && v.lang.startsWith('en'),

      // Google Chrome/Edge enhanced voices
      (v: SpeechSynthesisVoice) => (v.name.includes('Neural') || v.name.includes('Premium')) && v.lang.startsWith('en'),
      (v: SpeechSynthesisVoice) => v.name.includes('Enhanced') && v.lang.startsWith('en'),

      // General English voices with natural characteristics
      (v: SpeechSynthesisVoice) => v.lang === 'en-US' && v.localService,
      (v: SpeechSynthesisVoice) => v.lang === 'en-GB' && v.localService,
      (v: SpeechSynthesisVoice) => v.lang.startsWith('en') && v.localService,

      // Fallback to any English voice
      (v: SpeechSynthesisVoice) => v.lang.startsWith('en'),
    ];

    for (const preference of voicePreferences) {
      const voice = voices.find(preference);
      if (voice) {
        console.log('Selected voice:', voice.name, voice.lang);
        return voice;
      }
    }

    return voices[0] || null;
  }

  /**
   * Configure utterance for more natural speech
   */
  private configureUtterance(utterance: SpeechSynthesisUtterance): void {
    const voice = this.selectBestVoice();
    if (voice) {
      utterance.voice = voice;
    }

    // Slightly slower rate for better comprehension and naturalness
    utterance.rate = 0.85;

    // Slightly varied pitch for more natural sound
    utterance.pitch = 0.95;

    // Moderate volume
    utterance.volume = 0.9;

    // Add some variation to make it less monotone
    // Note: These values work better with higher-quality voices
    if (
      voice &&
      (voice.name.includes('Neural') || voice.name.includes('Enhanced') || voice.name.includes('Samantha'))
    ) {
      utterance.rate = 0.9; // Slightly faster for high-quality voices
      utterance.pitch = 1.0; // Let high-quality voices use their natural pitch
    }
  }

  /**
   * Start speaking text with enhanced naturalness
   */
  speak(text: string): void {
    // If we're already speaking, stop it first
    if (this.utterance) {
      this.stop();
    }

    // Preprocess text for better pronunciation
    const processedText = this.preprocessText(text);

    // Store the current text
    this.currentText = processedText;

    // Create a new utterance
    this.utterance = new SpeechSynthesisUtterance(processedText);

    // Configure for natural speech
    this.configureUtterance(this.utterance);

    // Setup event handlers
    this.utterance.onend = () => {
      this.currentVoiceState.next('idle');
      this.currentPlaybackPosition = 0;
    };

    this.utterance.onerror = event => {
      console.error('SpeechSynthesis error:', event);
      this.currentVoiceState.next('idle');
    };

    // Optional: Add boundary events for more responsive UI
    this.utterance.onboundary = event => {
      if (event.name === 'sentence') {
        // Could be used to highlight current sentence being read
        this.currentPlaybackPosition = event.charIndex;
      }
    };

    // Speak!
    this.synthesis.speak(this.utterance);
    this.currentVoiceState.next('playing');
  }

  /**
   * Pause the current speech
   */
  pause(): void {
    if (this.isPlaying()) {
      this.synthesis.pause();
      this.currentVoiceState.next('paused');
    }
  }

  /**
   * Resume paused speech
   */
  resume(): void {
    if (this.isPaused()) {
      this.synthesis.resume();
      this.currentVoiceState.next('playing');
    }
  }

  /**
   * Toggle play/pause state
   */
  toggle(text?: string): void {
    if (this.isPlaying()) {
      this.pause();
    } else if (this.isPaused()) {
      this.resume();
    } else if (text) {
      this.speak(text);
    }
  }

  /**
   * Stop speech completely
   */
  stop(): void {
    this.synthesis.cancel();
    this.utterance = null;
    this.currentVoiceState.next('idle');
    this.currentPlaybackPosition = 0;
  }
}
